import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Chip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import api from '../../../api/axios';
import RichTextEditor from '../../RichTextEditor';
import RichTextDisplay from '../../RichTextDisplay';
import { AudioPlayer } from '../../AudioPlayer';
import { EVENT_TYPE_COLORS, getEventIcon } from '../constants/eventConfig';
import type { TimelineEvent } from '../types/timeline.types';

interface LogEntryCardProps {
  event: TimelineEvent;
  isEditing: boolean;
  isNewlyCreated: boolean;
  isLastEvent: boolean;
  headers: any;

  // Edit state
  editEventDate: string;
  editLogContent: string;
  editLogFiles: File[];
  editLogExistingAttachments: any[];
  editLogAttachmentsToRemove: string[];

  // Edit state setters
  setEditEventDate: (date: string) => void;
  setEditLogContent: (content: string) => void;
  setEditLogFiles: (files: File[]) => void;
  setEditLogExistingAttachments: (attachments: any[]) => void;
  setEditLogAttachmentsToRemove: (ids: string[]) => void;

  // Event handlers
  onStartEdit: (event: TimelineEvent) => void;
  onCancelEdit: () => void;
  onSave: (event: TimelineEvent) => void;
  onDelete: (event: TimelineEvent) => void;
}

export const LogEntryCard: React.FC<LogEntryCardProps> = ({
  event,
  isEditing,
  isNewlyCreated,
  isLastEvent,
  headers,
  editEventDate,
  editLogContent,
  editLogFiles,
  editLogExistingAttachments,
  editLogAttachmentsToRemove,
  setEditEventDate,
  setEditLogContent,
  setEditLogFiles,
  setEditLogExistingAttachments,
  setEditLogAttachmentsToRemove,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
}) => {
  const handleDownloadAttachment = async (attachment: any) => {
    try {
      const response = await api.get(attachment.file_url, { headers });
      const downloadUrl = response.data.url;
      const isLocalUrl = downloadUrl.includes('localhost') || downloadUrl.includes('127.0.0.1');

      if (isLocalUrl) {
        const urlObj = new URL(downloadUrl);
        let relativePath = urlObj.pathname + urlObj.search;
        if (relativePath.startsWith('/api/')) {
          relativePath = relativePath.substring(5);
        }

        const fileResponse = await api.get(relativePath, {
          headers,
          responseType: 'blob'
        });

        const blob = new Blob([fileResponse.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', attachment.original_filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', attachment.original_filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error('Failed to download attachment:', error);
      alert('Failed to download attachment. Please try again.');
    }
  };

  return (
    <Box
      id={`event-${event.id}`}
      sx={{
        display: 'flex',
        position: 'relative',
        ...(isNewlyCreated && {
          animation: 'slideInFromTop 0.5s ease-out, highlightFade 2s ease-in-out',
          '@keyframes slideInFromTop': {
            '0%': {
              transform: 'translateY(-20px)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0)',
              opacity: 1,
            },
          },
          '@keyframes highlightFade': {
            '0%': {
              '& .event-card': {
                backgroundColor: 'primary.light',
                boxShadow: 3,
              }
            },
            '50%': {
              '& .event-card': {
                backgroundColor: 'primary.light',
                boxShadow: 3,
              }
            },
            '100%': {
              '& .event-card': {
                backgroundColor: event.is_important ? 'warning.50' : 'background.paper',
                boxShadow: 0,
              }
            },
          },
        }),
      }}
    >
      {/* Left: Date/Time Column */}
      <Box sx={{ width: 120, flexShrink: 0, pr: 2, pt: 1.5 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 600,
            color: 'text.secondary',
            fontSize: '0.75rem',
            lineHeight: 1.4,
          }}
        >
          {new Date(event.event_date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'text.secondary',
            fontSize: '0.7rem',
          }}
        >
          {new Date(event.event_date).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
          })}
        </Typography>
      </Box>

      {/* Timeline Line & Dot */}
      <Box sx={{ width: 40, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 1 }}>
        {/* Dot/Marker */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: EVENT_TYPE_COLORS['log_entry'],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            zIndex: 2,
            boxShadow: '0 0 0 4px white, 0 0 0 5px ' + EVENT_TYPE_COLORS['log_entry'],
          }}
        >
          {getEventIcon('log_entry', 'small')}
        </Box>
        {/* Vertical line extending down (hidden on last event) */}
        {!isLastEvent && (
          <Box
            sx={{
              width: 2,
              flex: 1,
              backgroundColor: 'divider',
              mt: -0.5,
            }}
          />
        )}
      </Box>

      {/* Right: Content Card */}
      <Box sx={{ flex: 1, minWidth: 0, pb: 3 }}>
        <Card
          className="event-card"
          sx={{
            border: '1px solid',
            borderColor: event.is_important ? 'warning.main' : 'divider',
            borderRadius: 2,
            backgroundColor: event.is_important ? 'warning.50' : 'background.paper',
            '&:hover': {
              boxShadow: 2,
            },
            transition: 'box-shadow 0.2s',
            position: 'relative',
          }}
        >
          <CardContent sx={{ p: 2 }}>
            {/* Edit button in top right */}
            <IconButton
              size="small"
              onClick={() => {
                if (isEditing) {
                  onCancelEdit();
                } else {
                  onStartEdit(event);
                }
              }}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                opacity: 0.7,
                '&:hover': { opacity: 1 },
                zIndex: 1,
              }}
            >
              {isEditing ? <CloseIcon sx={{ fontSize: 18 }} /> : <EditIcon sx={{ fontSize: 18 }} />}
            </IconButton>

            {/* Author & Tags Header */}
            <Box sx={{ mb: 2, pr: 4 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {event.author_name || 'Unknown'}
              </Typography>
              {event.metadata?.tags && (
                <Chip
                  label={event.metadata.tags}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    backgroundColor: 'action.selected',
                  }}
                />
              )}
            </Box>

            {/* Content Area */}
            <Box>
            {isEditing ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Edit Case Log Entry
                </Typography>

                {/* Date & Time */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                    Date & Time
                  </Typography>
                  <TextField
                    type="datetime-local"
                    size="small"
                    value={editEventDate}
                    onChange={(e) => setEditEventDate(e.target.value)}
                    sx={{ width: 250 }}
                  />
                </Box>

                {/* Content */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontWeight: 600 }}>
                    Content
                  </Typography>
                  <RichTextEditor
                    initialContent={editLogContent}
                    onChange={(content) => setEditLogContent(content)}
                    placeholder="Enter case log details..."
                    minHeight={150}
                    showSaveButton={false}
                  />
                </Box>

                {/* Existing Attachments */}
                {editLogExistingAttachments.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <AttachFileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        Existing Attachments ({editLogExistingAttachments.length})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {editLogExistingAttachments.map((attachment) => (
                        <Chip
                          key={attachment.id}
                          label={attachment.original_filename}
                          icon={<AttachFileIcon />}
                          onDelete={() => {
                            setEditLogAttachmentsToRemove([...editLogAttachmentsToRemove, attachment.id]);
                            setEditLogExistingAttachments(editLogExistingAttachments.filter(a => a.id !== attachment.id));
                          }}
                          onClick={() => handleDownloadAttachment(attachment)}
                          clickable
                          size="small"
                          sx={{
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              color: 'primary.contrastText',
                            },
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Add New Attachments */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <UploadIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Add New Attachments
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    size="small"
                  >
                    Upload Files
                    <input
                      type="file"
                      multiple
                      hidden
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setEditLogFiles([...editLogFiles, ...files]);
                      }}
                      accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.csv,.png,.jpg,.jpeg"
                    />
                  </Button>
                  {editLogFiles.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        {editLogFiles.length} file{editLogFiles.length !== 1 ? 's' : ''} to upload:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {editLogFiles.map((file, index) => (
                          <Chip
                            key={index}
                            label={file.name}
                            onDelete={() => setEditLogFiles(editLogFiles.filter((_, i) => i !== index))}
                            size="small"
                            icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CheckIcon />}
                    onClick={() => onSave(event)}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CloseIcon />}
                    onClick={onCancelEdit}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => onDelete(event)}
                    sx={{ ml: 'auto' }}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                {/* Log content */}
                {event.metadata?.content && (
                  <Box sx={{ mb: 2 }}>
                    <RichTextDisplay content={event.metadata.content} />
                  </Box>
                )}

                {/* Attachments */}
                {event.metadata?.attachments && event.metadata.attachments.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
                      {event.metadata.attachments.map((attachment: any) => {
                        const isAudio = attachment.original_filename?.toLowerCase().match(/\.(webm|mp3|wav|m4a|ogg)$/);

                        if (isAudio) {
                          return (
                            <Box key={attachment.id} sx={{ width: '50%' }}>
                              <AudioPlayer
                                attachmentId={attachment.id}
                                fileUrl={attachment.file_url}
                                filename={attachment.original_filename}
                                contentType={attachment.content_type}
                                headers={headers}
                              />
                            </Box>
                          );
                        }

                        return (
                          <Chip
                            key={attachment.id}
                            label={attachment.original_filename}
                            size="small"
                            icon={<AttachFileIcon sx={{ fontSize: 14 }} />}
                            onClick={() => handleDownloadAttachment(attachment)}
                            sx={{
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'primary.contrastText',
                              }
                            }}
                            clickable
                          />
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
